import { api } from "@signet/web-kit";
import type { Day, Schedule, Status } from "../shared/day.ts";

/* What the To-do pages read from /api/m/todo, typed once. Mirrors server/store.ts. */

export const BASE = "/api/m/todo";

export const PRIORITIES = ["urgent", "high", "medium", "low"] as const;
export type Priority = (typeof PRIORITIES)[number];
export const PRIORITY_LABEL: Record<Priority, string> = { urgent: "Urgent", high: "High", medium: "Medium", low: "Low" };

export type Person = { id: string; name: string };

export type Subitem = { id: string; name: string; done: boolean };

export type Todo = {
  id: string;
  name: string;
  notes: string;
  creator: string | null;
  priority: Priority | null;
  pinned: boolean;
  dueOn: string | null;
  assignee: Person | null;
  done: boolean;
  doneAt: string | null;
  doneBy: string | null;
  createdBy: string | null;
  createdAt: string;
  updatedBy: string | null;
  updatedAt: string;
  /** Who is working on it now, by name. */
  workingBy: string[];
  /** The checklist above the notes: open ones first, in the order added. */
  subitems: Subitem[];
};

export type TodoPatch = Partial<{
  name: string;
  notes: string;
  creator: string | null;
  priority: Priority | null;
  pinned: boolean;
  dueOn: string | null;
  assignee: string | null;
  done: boolean;
  /** With done: the to-do moved on to (Telegram), or null for none. */
  next: string | null;
}>;

export type Show = "open" | "mine" | "done" | "now";

export const todoKeys = {
  all: ["todo"] as const,
  list: (show: Show) => ["todo", "list", show] as const,
  people: ["todo", "people"] as const,
  // Outside "todo", so saving a to-do, which refreshes everything under it, does not ask for the roster again.
  creators: ["todo-creators"] as const,
  telegram: ["todo", "telegram"] as const,
};

export const fetchTodos = (show: Show) =>
  api<{ items: Todo[] }>(`${BASE}/items?${new URLSearchParams(show === "mine" ? { show: "open", mine: "1" } : { show })}`);
export const fetchPeople = () => api<{ people: Person[] }>(`${BASE}/people`);
/** The creators a to-do can name: only those on the roster in Creators (Jay, 2026-10-19). */
export const fetchCreators = () => api<{ creators: string[] }>(`${BASE}/creators`);
export const createTodo = (input: TodoPatch & { name: string }) => api<{ item: Todo }>(`${BASE}/items`, { body: input });
/** What happened on Telegram: off when the person doesn't post there or isn't set up. */
export type Delivery = "sent" | "edited" | "failed" | "off";

export const updateTodo = (id: string, patch: TodoPatch) => api<{ item: Todo; telegram?: Delivery }>(`${BASE}/items/${encodeURIComponent(id)}`, { method: "PATCH", body: patch });
export const moveTodo = (id: string, place: { before: string } | { after: string }) => api(`${BASE}/items/${encodeURIComponent(id)}/move`, { body: place });
export const deleteTodo = (id: string) => api(`${BASE}/items/${encodeURIComponent(id)}`, { method: "DELETE" });

const subitemsOf = (id: string) => `${BASE}/items/${encodeURIComponent(id)}/subitems`;
export const subitemApi = {
  add: (id: string, name: string) => api<{ item: Todo }>(subitemsOf(id), { body: { name } }),
  update: (id: string, sub: string, patch: { name?: string; done?: boolean }) =>
    api<{ item: Todo }>(`${subitemsOf(id)}/${encodeURIComponent(sub)}`, { method: "PATCH", body: patch }),
  remove: (id: string, sub: string) => api<{ item: Todo }>(`${subitemsOf(id)}/${encodeURIComponent(sub)}`, { method: "DELETE" }),
};

export type TelegramStatus = {
  connected: boolean;
  hasKeys: boolean;
  account: string | null;
  chat: { id: string; title: string } | null;
  topic: { id: number; title: string } | null;
  watch: string;
  watchFound: boolean;
  pending: boolean;
  working: string | null;
  day: Day;
  lunchAt: string | null;
  schedule: Schedule;
};

/** This browser's time zone: the day's statuses start over at its midnight. */
const timeZone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    return "UTC";
  }
};

export const telegramApi = {
  status: () => api<TelegramStatus>(`${BASE}/telegram?${new URLSearchParams({ tz: timeZone() })}`),
  sendCode: (input: { apiId?: number; apiHash?: string; phone: string }) => api<{ viaApp: boolean }>(`${BASE}/telegram/code`, { body: input }),
  signIn: (input: { code: string; password?: string }) => api<TelegramStatus | { needsPassword: true }>(`${BASE}/telegram/sign-in`, { body: input }),
  groups: () => api<{ groups: { id: string; title: string }[] }>(`${BASE}/telegram/groups`),
  topics: (chat: string) => api<{ topics: { id: number; title: string }[] }>(`${BASE}/telegram/topics?${new URLSearchParams({ chat })}`),
  target: (input: { chatId: string; topicId: number; watch: string }) => api<TelegramStatus>(`${BASE}/telegram/target`, { method: "PUT", body: input }),
  retry: () => api<{ telegram: Delivery }>(`${BASE}/telegram/retry`, { method: "POST", body: {} }),
  signOut: () => api<TelegramStatus>(`${BASE}/telegram`, { method: "DELETE" }),
  schedule: (schedule: Schedule) => api<TelegramStatus>(`${BASE}/telegram/schedule?${new URLSearchParams({ tz: timeZone() })}`, { method: "PUT", body: schedule }),
  say: (status: Status) => api<{ telegram: Delivery; day: Day }>(`${BASE}/telegram/status`, { body: { status, tz: timeZone() } }),
  work: (todoId: string | null) => api<{ working: string | null; telegram: Delivery }>(`${BASE}/working`, { body: { todoId } }),
};

/** Today as YYYY-MM-DD in this browser's own day. */
export function todayISO(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

/** "Due today", "Due tomorrow", "Overdue, Oct 6", "Oct 10, 2026", as Ben's layout writes them. */
export function dueLabel(dueOn: string, today = todayISO()): { text: string; tone: "late" | "today" | "soon" | "later" } {
  const days = Math.round((Date.parse(`${dueOn}T00:00:00Z`) - Date.parse(`${today}T00:00:00Z`)) / 86_400_000);
  const date = new Date(`${dueOn}T00:00:00`);
  if (days < 0) return { text: `Overdue, ${date.toLocaleDateString(undefined, { month: "short", day: "numeric" })}`, tone: "late" };
  if (days === 0) return { text: "Due today", tone: "today" };
  if (days === 1) return { text: "Due tomorrow", tone: "soon" };
  return { text: date.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }), tone: "later" };
}
